import { stores } from '@stores/.';
import { LanguageType } from '@stores/transformationsList';
import { Traits } from '@stores/user';
import { Category, SubCategories } from '@stores/healthDashboard/types';
import { ResourceCategory, ResourceType } from '@stores/alertDefinitionsList/types';
import { ChannelType } from '@components/alertNotifications/stores/types';
import type { IdentifyTraits } from '@rudderstack/analytics-js';

export * as webapp from './webapp';

type Track<X, Y> = {
  event: X;
  properties: Y;
};

type ideTrackEvent = {
  projectId: string;
  clickCtaName: string;
};

type TrackPayload =
  | Track<'New Integration requested', { integration: string }>
  | Track<
      'destinationCreated',
      {
        destinationId: string;
        destinationType: string;
        workspaceId?: string;
        organizationId?: string;
      }
    >
  | Track<'destinationSetupStarted', { destinationType: string }>
  | Track<'destinationSetupStepCompleted', { step: number; destinationType: string }>
  | Track<'viewedLiveEvents', { resourceType: 'source' | 'destination' | 'transformation' }>
  | Track<'inviteAccepted', { inviteEmail: string }>
  | Track<'gettingStartedChecklistCompleted', Record<string, never>>
  | Track<'userCompletedOnboarding', Traits>
  | Track<'sourceSetupStarted', { sourceType: string }>
  | Track<'sourceSetupStepCompleted', { step: number; sourceType: string }>
  | Track<'sourceCreated', { sourceId: string; sourceType: string }>
  | Track<'ads_cnv_app_signup', Record<string, never>>
  | Track<'clickWebApp', Record<string, string>>
  | Track<'mutinySignup', { spz1: string }>
  | Track<'sourceDisabled', { sourceId: string; sourceType: string }>
  | Track<'sourceDeleted', { sourceId: string; sourceType: string }>
  | Track<'destinationDeleted', { destinationId: string; destinationType: string }>
  | Track<'destinationDisabled', { destinationId: string; destinationType: string }>
  | Track<'clickNewTransformationButton', { usersId: string }>
  | Track<
      'newTransformationViewed',
      {
        usersId: string;
        transformationTemplateType: 'custom' | 'template';
      }
    >
  | Track<
      'newTransformationCreated',
      {
        transformationId: string;
        transformationTemplateType: 'custom' | 'template';
        transformationLanguageType: LanguageType;
      }
    >
  | Track<
      'transformationConnectedToDestination',
      {
        destinationId: string;
        transformationId: string;
        destinationMode: 'Cloud Mode' | 'Device Mode';
      }
    >
  | Track<
      'transformationDisconnectedFromDestination',
      { destinationId: string; transformationId: string }
    >
  | Track<'transformationLibraryCreated', { libraryId: string }>
  | Track<
      'transformationTested',
      { transformationId?: string; transformationLanguageType: LanguageType }
    >
  | Track<
      'ImportEventTypeTesting',
      {
        transformationId?: string;
        importEventType: string;
      }
    >
  | Track<
      'transformationLogs',
      { transformationId?: string; transformationLanguageType: LanguageType }
    >
  | Track<
      'transformationSplitScreen',
      {
        transformationId?: string;
        transformationLanguageType: string;
        splitDirection: 'vertical' | 'horizontal';
      }
    >
  | Track<
      'transformationDeleted',
      {
        transformationId?: string;
        transformationLanguageType: LanguageType;
        destinationMode: 'Cloud Mode' | 'Device Mode';
      }
    >
  | Track<'transformationNotifications', { transformationId?: string }>
  | Track<'transformationEventsPageViewed', { transformationId?: string }>
  | Track<
      'transformationDiffViewed',
      { transformationId?: string; transformationLanguageType: LanguageType }
    >
  | Track<'login', { authMethod?: string }>
  | Track<'healthTabViewed', { resourceCategory: Category }>
  | Track<
      'resourceSelected',
      {
        resourceCategory: Category;
        sourceId?: string;
        destinationId?: string;
        trackingPlanId?: string;
      }
    >
  | Track<
      'destinationTypeFilterClicked',
      {
        filter: SubCategories[Category.EVENT_STREAM];
      }
    >
  | Track<
      'statusFilterClicked',
      {
        resourceCategory: Category.EVENT_STREAM | Category.TRACKING_PLAN;
        filter: 'failures' | 'violations' | 'all';
      }
    >
  | Track<
      'retlFilterClicked',
      {
        type: 'source' | 'destination' | 'status';
        sourceId?: string;
        destinationId?: string;
        status?: string;
      }
    >
  | Track<
      'visitResourceClicked',
      {
        resourceCategory: Category;
        sourceId?: string;
        destinationId?: string;
      }
    >
  | Track<'retryInitiated', { destinationId: string; errorCategory?: string; sourceId?: string }>
  | Track<
      'workspaceAlertsViewed',
      {
        resourceCategory: ResourceCategory;
      }
    >
  | Track<
      'resourceAlertsViewed',
      {
        resourceCategory: ResourceCategory;
        resourceType: ResourceType;
        sourceId?: string;
        destinationId?: string;
        profileId?: string;
      }
    >
  | Track<
      'channelToggled',
      {
        channelType: ChannelType;
        resourceCategory: ResourceCategory;
        toggleValue: boolean;
      }
    >
  | Track<
      'alertToggled',
      {
        resourceCategory: ResourceCategory;
        alertName: string;
        toggleValue: boolean;
        resourceType?: ResourceType;
        sourceId?: string;
        destinationId?: string;
        profileId?: string;
      }
    >
  | Track<
      'resetCustomAlertsClicked',
      {
        resourceCategory: ResourceCategory;
        resourceType: ResourceType;
        sourceId?: string;
        destinationId?: string;
        profileId?: string;
      }
    >
  | Track<
      'channelOverrideCreated',
      {
        channelType: ChannelType;
        resourceCategory: ResourceCategory;
        resourceType: ResourceType;
        active: boolean;
        sourceId?: string;
        destinationId?: string;
        profileId?: string;
      }
    >
  | Track<
      'channelOverrideReset',
      {
        channelType: ChannelType;
        resourceCategory: ResourceCategory;
        resourceId: string;
      }
    >
  | Track<
      'alertOverrideCreated',
      {
        resourceCategory: ResourceCategory;
        resourceType: ResourceType;
        definitionId: string;
        active: boolean;
        sourceId?: string;
        destinationId?: string;
        profileId?: string;
      }
    >
  | Track<
      'alertOverrideReset',
      {
        resourceCategory: ResourceCategory;
        resourceType: ResourceType;
        definitionId: string;
        sourceId?: string;
        destinationId?: string;
        profileId?: string;
      }
    >
  | Track<
      'ideProjectCreate',
      {
        clickCtaName: string;
      }
    >
  | Track<'ideProjectRun', ideTrackEvent>
  | Track<'ideProjectCommit', ideTrackEvent>
  | Track<'ideProjectSave', ideTrackEvent>
  | Track<'ideProjectOpen', ideTrackEvent>
  | Track<'ideProjectCompile', ideTrackEvent>
  | Track<
      'botDetectionToggled' | 'botWorkspaceEventManagementToggled',
      {
        toggleValue: boolean;
      }
    >
  | Track<
      'botWorkspaceEventManagementModeChanged',
      {
        radioValue: string;
      }
    >
  | Track<
      'botSourceListEventManagementAdded' | 'botSourceListEventManagementRemoved',
      {
        sourceId: string;
        sourceName: string;
      }
    >
  | Track<
      'botSourceEventManagementToggled',
      {
        sourceId: string;
        sourceName: string;
        toggleValue: boolean;
      }
    >
  | Track<
      'botSourceEventManagementModeChanged',
      {
        sourceId: string;
        sourceName: string;
        radioValue: string;
      }
    >
  | Track<
      'copilotServerLaunch',
      {
        clickCtaName: string;
      }
    >
  | Track<
      'profilesArtifactsAccessed',
      {
        projectId: string;
        runId: string;
        runStatus: string;
      }
    >
  | Track<
      'profilesAllArtifactsDownloaded',
      {
        projectId?: string;
        runId: string;
        runStatus?: string;
        source: string;
      }
    >
  | Track<
      'signupStarted',
      {
        flowVersion: string;
        source: string;
        utmSource?: string;
        utmCampaign?: string;
        utmMedium?: string;
        referrer?: string;
      }
    >
  | Track<
      'signupCompleted',
      {
        flowVersion: string;
        authMethod: string;
        userRole: string;
        heardAboutSource?: string;
        organizationName: string;
        region: string;
        timeToSignupSeconds?: number;
      }
    >
  | Track<
      'signupFailed',
      {
        flowVersion: string;
        authMethod: string;
        errorType: string;
        errorMessage?: string;
      }
    >
  | Track<'workspaceSetupStarted', { flowVersion: string; timeSinceSignupSeconds?: number }>
  | Track<
      'workspaceSetupStepViewed',
      { flowVersion: string; step: number; stepName: string; isReturnVisit: boolean }
    >
  | Track<
      'workspaceSetupStepCompleted',
      {
        flowVersion: string;
        step: number;
        stepName: string;
        timeOnStepSeconds: number;
        [key: string]: string | number | boolean | string[] | undefined;
      }
    >
  | Track<
      'workspaceSetupStepSkipped',
      { flowVersion: string; step: number; stepName: string; timeOnStepSeconds: number }
    >
  | Track<
      'workspaceSetupCompleted',
      {
        flowVersion: string;
        stepsCompleted: number;
        totalDurationSeconds: number;
        [key: string]: string | number | boolean | string[] | undefined;
      }
    >;

const enhanceEvent = (
  props: TrackPayload['properties'] & {
    userId?: never;
  },
) => {
  const { userStore, organizationStore, workspaceStore } = stores;
  const { loggedIn } = userStore;
  if (loggedIn) {
    return {
      workspaceId: workspaceStore.id,
      organizationId: organizationStore.id || undefined,
      ...props,
    };
  }
  return props;
};

export const Analytics = {
  identify: (userId: string, traits: IdentifyTraits) => {
    window?.rudderanalytics?.identify(userId, traits);
  },

  track: (payload: TrackPayload) => {
    const { event, properties } = payload;
    window?.rudderanalytics?.track(event, enhanceEvent(properties));
  },

  group: (groupId: string, traits: IdentifyTraits) => {
    window?.rudderanalytics?.group(groupId, traits);
  },
};
